import { advisoryForLine } from '@shared/lineItems';

// Classifies line items for a posting-confirmation modal (86d3hm2vr). Module-agnostic:
// both the Sales Order and Cutting List modals reduce their lines to PostingLineInput and
// render the result. Three buckets:
//   • blocking   — NetSuite can't accept it (no matched SKU / qty ≤ 0 / — SO — no price).
//                  The modal disables the confirm while any exist.
//   • excluded   — will silently NOT post (e.g. CL kg-unit lines the service drops). Shown
//                  for transparency; does not block.
//   • attention  — postable but flags a sales spec rule (advisoryForLine). Proceed via the
//                  second-confirm checkbox.

export type PostingLineSeverity = 'blocking' | 'excluded' | 'attention';

export interface PostingLineInput {
  /** Stable key — SO clientId / Quotation id. */
  key: string;
  /** Human-readable line label, e.g. "16mm × 6m · G40 · 500 pcs". */
  label: string;
  // Spec fields (fed to advisoryForLine).
  diameterMm: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
  // Postability.
  matchedItemId: string | null;
  quantity: number | null;
  /** SO passes the resolvable-price gate; leave undefined where price isn't a gate (CL). */
  hasResolvablePrice?: boolean;
  /** Caller-computed exclusion (e.g. CL kg-unit line). When set (and not blocking), the line
   *  is bucketed as excluded with this reason. */
  excludedReason?: string | null;
  /** Extra attention reasons the shared rules can't derive (e.g. SO over-guideline). */
  extraAttentionReasons?: string[];
}

export interface PostingLineFlag {
  key: string;
  label: string;
  severity: PostingLineSeverity;
  reasons: string[];
}

export interface ClassifiedPostingLines {
  blocking: PostingLineFlag[];
  excluded: PostingLineFlag[];
  attention: PostingLineFlag[];
}

function blockingReasons(line: PostingLineInput): string[] {
  const reasons: string[] = [];
  if (!line.matchedItemId) reasons.push('No matched SKU');
  if ((line.quantity ?? 0) <= 0) reasons.push('Quantity is 0');
  if (line.hasResolvablePrice === false) reasons.push('No price level');
  return reasons;
}

export function classifyPostingLines(lines: PostingLineInput[]): ClassifiedPostingLines {
  const result: ClassifiedPostingLines = { blocking: [], excluded: [], attention: [] };

  for (const line of lines) {
    const blockers = blockingReasons(line);
    if (blockers.length > 0) {
      result.blocking.push({ key: line.key, label: line.label, severity: 'blocking', reasons: blockers });
      continue;
    }

    if (line.excludedReason) {
      result.excluded.push({
        key: line.key,
        label: line.label,
        severity: 'excluded',
        reasons: [line.excludedReason],
      });
      continue;
    }

    const reasons: string[] = [];
    const rule = advisoryForLine(line);
    if (rule) reasons.push(rule.label);
    if (line.extraAttentionReasons) reasons.push(...line.extraAttentionReasons);
    if (reasons.length > 0) {
      result.attention.push({ key: line.key, label: line.label, severity: 'attention', reasons });
    }
  }

  return result;
}

export function hasPostingFlags(c: ClassifiedPostingLines): boolean {
  return c.blocking.length > 0 || c.excluded.length > 0 || c.attention.length > 0;
}

/** Compact human label for a rebar line in a posting modal, e.g. "16mm × 6m · G40 · 500 pcs".
 *  Falls back to the matched SKU description when the spec is thin. */
export function postingLineLabel(l: {
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: string | null;
  quantity: number | null;
  unit: string;
  matchedSalesDescription?: string | null;
}): string {
  const spec = [
    l.diameterMm != null ? `${l.diameterMm}mm` : null,
    l.lengthM != null ? `${l.lengthM}m` : null,
    l.gradeCode ? `G${l.gradeCode}` : null,
  ]
    .filter(Boolean)
    .join(' × ');
  const qty = l.quantity != null ? `${l.quantity} ${l.unit}` : null;
  const head = spec || l.matchedSalesDescription?.trim() || 'Line item';
  return [head, qty].filter(Boolean).join(' · ');
}
